import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMoneyTarget, saveMoneyTarget } from '../services/yearPlanTargetService';
import { setHasUnsavedTarget } from '../store/application/applicationSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';

interface UseYearPlanTargetResult {
  target: number | null;
  setTarget: (target: number | null) => void;
  isTargetDirty: boolean;
  // False when the write was refused, so the caller can keep the field on screen
  // for a retry rather than closing edit mode over a figure that never landed.
  saveTarget: () => boolean;
  discardTarget: () => void;
}

/**
 * The year's money target, edited on the year plan.
 *
 * Kept beside the year plan's own local state rather than inside it, because the
 * target is not part of the treatment plan payload yet. It still rides the same
 * accept and discard actions, which is the behaviour decided on 2026-08-20: one
 * lock, one Save, one Discard. The cost accepted with that decision is that
 * Discard all reverts the target too.
 */
export function useYearPlanTarget(
  locationId: string | undefined,
  year: number
): UseYearPlanTargetResult {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [savedTarget, setSavedTarget] = useState<number | null>(null);
  const [target, setTarget] = useState<number | null>(null);

  // Re-read whenever the location or year changes, so switching either does not
  // carry the previous scenario's target into the new one. Adjusted during
  // render rather than in an effect, the same pattern useKronerInput uses to
  // re-derive its text: an effect would render the previous scenario's figure
  // once before correcting it.
  const scenarioKey = locationId ? `${locationId}:${String(year)}` : null;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  if (scenarioKey !== loadedKey) {
    setLoadedKey(scenarioKey);
    const stored = locationId ? getMoneyTarget(locationId, year) : null;
    setSavedTarget(stored);
    setTarget(stored);
  }

  // Only marks the target clean once the write has actually landed. A failed
  // write leaves it dirty, so the unsaved-changes guard keeps protecting it
  // rather than the planner being shown a saved figure that is not saved.
  //
  // A refusal is reported here rather than returned silently: the browser
  // refusing storage is nothing the planner can infer from the screen, and the
  // figure is a government allocation they just typed.
  const saveTarget = useCallback((): boolean => {
    if (!locationId) return true;
    if (!saveMoneyTarget(locationId, year, target)) {
      dispatch(showError({ message: t('year-plan-target-save-error') }));
      return false;
    }
    setSavedTarget(target);
    return true;
  }, [locationId, year, target, dispatch, t]);

  const discardTarget = useCallback((): void => {
    setTarget(savedTarget);
  }, [savedTarget]);

  const isTargetDirty = target !== savedTarget;

  // Published to the store because the toolbar that has to react to it — Save's
  // enabled state, the discard confirmation — sits inside the Gantt, far from
  // this hook's caller. Kept in its own field rather than OR'd into the plan's:
  // see the note on hasUnsavedTarget in applicationSlice.
  useEffect(() => {
    dispatch(setHasUnsavedTarget(isTargetDirty));
  }, [dispatch, isTargetDirty]);

  // Leaving the page takes the field with it, so a dirty target must not outlive
  // it — the next scenario's toolbar would offer to save a figure that is no
  // longer on screen.
  useEffect(() => {
    return () => {
      dispatch(setHasUnsavedTarget(false));
    };
  }, [dispatch]);

  return {
    target,
    setTarget,
    isTargetDirty,
    saveTarget,
    discardTarget
  };
}
